import type { AxiosResponse, AxiosPromise, AxiosError } from 'axios';

export type AxiosPromiseBuilder<T> = () => AxiosPromise<T>;
type OnRetry = (currentAttempt: number) => void;

// Backoff between attempts when the 429 carries no usable Retry-After header, plus up to this much
// jitter so devices behind one IP that were throttled together do not retry in the same instant.
const RETRY_BACKOFF_MS: ReadonlyArray<number> = [2000, 5000, 15000];
const RETRY_JITTER_MS = 1000;
// Never wait longer than this on a single Retry-After, however large the header is.
const MAX_RETRY_AFTER_MS = 60_000;

export const isRateLimited = (err: unknown): boolean => (err as AxiosError | undefined)?.response?.status === 429;

/**
 * Milliseconds the server asked us to wait, read from a 429's `Retry-After` header
 * (delay in seconds, or an HTTP-date). `undefined` when the header is missing or unreadable.
 */
export const getRetryAfterMs = (err: unknown, now: number = Date.now()): number | undefined => {
  const header = (err as AxiosError | undefined)?.response?.headers?.['retry-after'];
  if (header === undefined || header === null || header === '') return undefined;
  const value = String(header).trim();
  if (value === '') return undefined;
  const seconds = Number(value);
  const ms = Number.isFinite(seconds) ? seconds * 1000 : Date.parse(value) - now;
  if (!Number.isFinite(ms)) return undefined;
  return Math.min(Math.max(ms, 0), MAX_RETRY_AFTER_MS);
};

export async function executeWithAuth0Retry<T>(builder: AxiosPromiseBuilder<T>, onRetry?: OnRetry): Promise<AxiosResponse<T>> {
  return new Auth0Retryer(builder, onRetry).execute();
}

/**
 * Runs an Auth0 / auth proxy request, retrying only on HTTP 429. Each retry waits for the
 * server's `Retry-After` when it sends one, else the next step of RETRY_BACKOFF_MS.
 * Any other failure, and a 429 after the last retry, rejects with the original error.
 */
export class Auth0Retryer<T> {
  private _onRetry?: OnRetry;
  private _success: boolean = false;
  private _attemptCount: number = 1;
  public readonly auditTrail: Array<{ attemptCount: number; attemptAt: number }> = [];
  public readonly builder: AxiosPromiseBuilder<T>;

  constructor(builder: AxiosPromiseBuilder<T>, onRetry?: OnRetry) {
    this.builder = builder;
    this._onRetry = onRetry;
  }

  public get attemptCount() {
    return this._attemptCount;
  }

  public get success() {
    return this._success;
  }

  public async execute(): Promise<AxiosResponse<T>> {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        this._audit();
        const response = await this.builder();
        this._success = true;
        return response;
      } catch (err) {
        const retryIndex = this._attemptCount - 1;
        // That's outside of our jurisdiction
        if (!isRateLimited(err) || retryIndex >= RETRY_BACKOFF_MS.length) throw err;

        const currentAttempt = this._attemptCount;
        this._attemptCount++;
        this._onRetry?.(currentAttempt);
        await this._wait(getRetryAfterMs(err) ?? RETRY_BACKOFF_MS[retryIndex] + Math.random() * RETRY_JITTER_MS);
      }
    }
  }

  private _wait(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private _audit() {
    this.auditTrail.push({
      attemptCount: this.attemptCount,
      attemptAt: Date.now(),
    });
  }
}
